import type {
  Filter,
  FilterOption,
  FilterOptionsDictionary,
  FilterValue,
  FilterValueDictionary
} from '@features/regional-overview/regional-overview.models';
import { ALL_FILTER_VALUE, RegionalOverviewFilterType } from '@features/regional-overview/regional-overview.models';
import type { Feature, Geometry } from 'geojson';
import { buildFilterOptionLabelKey, type FilterLabelContext } from './filter-labels.utils';

const isAllValue = (value: FilterValue): boolean => {
  if (value === undefined) return true;
  if (value === ALL_FILTER_VALUE) return true;
  if (Array.isArray(value) && value.length === 1 && value[0] === ALL_FILTER_VALUE) return true;
  return false;
};

interface DeriveFiltersStateInput {
  enabledFilters: Filter[];
  features: Feature<Geometry, unknown>[];
  context: Record<string, unknown>;
  values: FilterValueDictionary;
  appliedValues: FilterValueDictionary;
}

interface DeriveFiltersStateOutput {
  options: FilterOptionsDictionary;
  values: FilterValueDictionary;
  appliedValues: FilterValueDictionary;
}

interface ComputeFilteredFeaturesInput {
  enabledFilters: Filter[];
  features: Feature<Geometry, unknown>[];
  context: Record<string, unknown>;
  appliedValues: FilterValueDictionary;
}

const dedupe = (values: string[]): string[] => Array.from(new Set(values));

const defaultSortOptions = (options: FilterOption[]): FilterOption[] => [...options].sort((a, b) => a.value.localeCompare(b.value));

const isValueValid = (value: FilterValue, filter: Filter, options: FilterOption[]): boolean => {
  if (value === undefined) return false;
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    if (value[0] === ALL_FILTER_VALUE) return filter.includeAllOption === true;
    if (filter.freeform) return value.every(entry => entry !== undefined);
    return value.every(v => typeof v === 'string' && options.some(o => o.value === v));
  }
  if (value === ALL_FILTER_VALUE) return filter.includeAllOption === true;
  if (filter.freeform) return true;
  if (typeof value === 'string') return options.some(o => o.value === value);
  return true;
};

const isUnset = (value: FilterValue): boolean => {
  if (value === undefined) return true;
  return Array.isArray(value) && value.length === 0;
};

const firstAvailable = (filter: Filter, options: FilterOption[]): FilterValue => {
  if (options.length === 0) return undefined;
  const first = options[0].value;
  return Array.isArray(filter.defaultValue) ? [first] : first;
};

export const resolveDefaultFilterValue = (filter: Filter, options: FilterOption[]): FilterValue => {
  if (filter.defaultValue === undefined) return undefined;
  if (isValueValid(filter.defaultValue, filter, options)) return filter.defaultValue;
  return firstAvailable(filter, options);
};

export const deriveOptionsFor = (
  filter: Filter,
  features: Feature<Geometry, unknown>[],
  context: Record<string, unknown>
): FilterOption[] => {
  const raw: string[] = [];
  features.forEach(feature => {
    filter.getValues(feature, context).forEach(value => {
      if (value) raw.push(value);
    });
  });
  const options = dedupe(raw).map(value => ({
    value,
    labelKey: buildFilterOptionLabelKey(filter.type, value, context as FilterLabelContext)
  }));
  return filter.sortOptions ? filter.sortOptions(options) : defaultSortOptions(options);
};

export const deriveFiltersState = ({
  enabledFilters,
  features,
  context,
  values,
  appliedValues
}: DeriveFiltersStateInput): DeriveFiltersStateOutput => {
  const nextOptions: FilterOptionsDictionary = {};
  const nextValues: FilterValueDictionary = { ...values };
  const nextApplied: FilterValueDictionary = { ...appliedValues };
  let seededApplied = false;

  const hasNoFeatures = features.length === 0;

  enabledFilters.forEach(filter => {
    const opts = deriveOptionsFor(filter, features, context);
    nextOptions[filter.type] = opts;

    if (hasNoFeatures && !isUnset(nextValues[filter.type])) return;

    if (!isValueValid(nextValues[filter.type], filter, opts)) {
      const seeded = resolveDefaultFilterValue(filter, opts);
      nextValues[filter.type] = seeded;
      nextApplied[filter.type] = seeded;
      seededApplied = true;
    } else if (!isValueValid(nextApplied[filter.type], filter, opts)) {
      nextApplied[filter.type] = resolveDefaultFilterValue(filter, opts);
      seededApplied = true;
    }
  });

  return {
    options: nextOptions,
    values: nextValues,
    appliedValues: seededApplied ? nextApplied : appliedValues
  };
};

const runPrepare = (
  working: Feature<Geometry, unknown>[],
  filter: Filter,
  appliedValues: FilterValueDictionary,
  context: Record<string, unknown>
): Feature<Geometry, unknown>[] => {
  const prepare = filter.prepare;
  if (!prepare) return working;
  const value = appliedValues[filter.type];
  return working.map(feature => prepare(feature, value, context));
};

interface SortState {
  filtersByType: Map<RegionalOverviewFilterType, Filter>;
  alreadyAdded: Set<RegionalOverviewFilterType>;
  currentlyAdding: Set<RegionalOverviewFilterType>;
  ordered: Filter[];
}

const addFilterAfterItsDeps = (filter: Filter, state: SortState): void => {
  if (state.alreadyAdded.has(filter.type)) return;
  if (state.currentlyAdding.has(filter.type)) return;

  state.currentlyAdding.add(filter.type);

  (filter.dependsOn ?? []).forEach(depType => {
    const dep = state.filtersByType.get(depType);
    if (dep) addFilterAfterItsDeps(dep, state);
  });

  state.currentlyAdding.delete(filter.type);
  state.alreadyAdded.add(filter.type);
  state.ordered.push(filter);
};

export const sortFiltersByDependencies = (filters: Filter[]): Filter[] => {
  const state: SortState = {
    filtersByType: new Map(filters.map(filter => [filter.type, filter])),
    alreadyAdded: new Set(),
    currentlyAdding: new Set(),
    ordered: []
  };

  filters.forEach(filter => addFilterAfterItsDeps(filter, state));

  return state.ordered;
};

export const areFilterValuesEqual = (a: FilterValueDictionary, b: FilterValueDictionary): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => Object.is(a[key], b[key]));
};

export const areFeatureListsEqual = (a: Feature<Geometry, unknown>[], b: Feature<Geometry, unknown>[]): boolean =>
  a.length === b.length && a.every((feature, index) => feature === b[index]);

export const computeFilteredFeatures = ({
  enabledFilters,
  features,
  context,
  appliedValues
}: ComputeFilteredFeaturesInput): Feature<Geometry, unknown>[] => {
  if (!enabledFilters.length) return features;

  const orderedFilters = sortFiltersByDependencies(enabledFilters);

  let working: Feature<Geometry, unknown>[] = features;
  orderedFilters.forEach(filter => {
    working = runPrepare(working, filter, appliedValues, context);
  });

  return working.filter(feature =>
    enabledFilters.every(filter => {
      const value = appliedValues[filter.type];
      if (isAllValue(value)) return true;
      return filter.matches(feature, value, context, appliedValues);
    })
  );
};
